import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useErrorContext } from '@/modules/admin/context/ErrorContext';
import AdminPortalLayout from '@/modules/admin/components/AdminPortalLayout';
import { useToast } from '@/hooks/useToast';
import { captureError } from '@/utils/errorHandler';

export default function AdminErrorsPage() {
  const { authFetch } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast, toasts, hideToast } = useToast();
  const {
    errors,
    summary,
    pagination,
    filters,
    activeErrorId,
    loading: contextLoading,
    setErrors,
    setSummary,
    setPagination,
    setFilters,
    setActiveErrorId,
    loadErrors,
    saveScrollPosition,
    restoreScrollPosition,
    clearActiveError,
  } = useErrorContext();
  
  const [filteredAppName, setFilteredAppName] = useState(null);
  const [loading, setLoading] = useState(true);
  const errorRefs = useRef({});
  const listContainerRef = useRef(null);

  // Get filter from URL
  const statusFilter = searchParams.get('status') || '';
  const sourceFilter = searchParams.get('source') || '';
  const searchQuery = searchParams.get('search') || '';
  const appIdFilter = searchParams.get('appId') || '';
  const page = searchParams.get('page') || '1';

  // Sync filters with context
  useEffect(() => {
    setFilters({
      status: statusFilter,
      source: sourceFilter,
      search: searchQuery,
      appId: appIdFilter,
    });
  }, [statusFilter, sourceFilter, searchQuery, appIdFilter, setFilters]);

  // Fetch app name when filtering by appId
  useEffect(() => {
    if (appIdFilter) {
      authFetch(`/api/admin/app?appId=${appIdFilter}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.name) {
            setFilteredAppName(data.name);
          } else {
            setFilteredAppName(null);
          }
        })
        .catch(() => setFilteredAppName(null));
    } else {
      setFilteredAppName(null);
    }
  }, [appIdFilter, authFetch]);

  // Load errors when URL params change
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await loadErrors({
          status: statusFilter,
          source: sourceFilter,
          search: searchQuery,
          appId: appIdFilter,
          page: parseInt(page, 10),
        });
      } catch (error) {
        captureError(error, {
          action: 'loadErrors',
          component: 'AdminErrorsPage',
        });
        console.error('Error loading errors:', error);
        showToast('Failed to load errors', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sourceFilter, searchQuery, appIdFilter, page]);

  // Restore scroll position and highlight active error when returning from detail page
  useEffect(() => {
    if (activeErrorId && errors.length > 0) {
      // Wait for DOM to render
      requestAnimationFrame(() => {
        const errorElement = errorRefs.current[activeErrorId];
        if (errorElement) {
          // Scroll to active error
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear active error after highlighting (3 seconds)
          setTimeout(() => {
            setActiveErrorId(null);
          }, 3000);
        } else {
          // If error not found in current page, restore scroll position
          restoreScrollPosition();
        }
      });
    } else if (!activeErrorId) {
      // Restore scroll position if no active error
      restoreScrollPosition();
    }
  }, [activeErrorId, errors, restoreScrollPosition, setActiveErrorId]);

  // Save scroll position before navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveScrollPosition();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveScrollPosition]);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      await loadErrors({
        status: statusFilter,
        source: sourceFilter,
        search: searchQuery,
        appId: appIdFilter,
        page: parseInt(page, 10),
      });
    } catch (error) {
      captureError(error, {
        action: 'handleRefresh',
        component: 'AdminErrorsPage',
      });
      console.error('Error refreshing:', error);
      showToast('Failed to refresh errors', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleErrorClick = (errorId) => {
    // Save scroll position before navigating
    saveScrollPosition();
    
    // Build URL with all query params
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (sourceFilter) params.set('source', sourceFilter);
    if (searchQuery) params.set('search', searchQuery);
    if (appIdFilter) params.set('appId', appIdFilter);
    if (page !== '1') params.set('page', page);
    
    const queryString = params.toString();
    navigate(`/admin-portal/errors/${errorId}${queryString ? `?${queryString}` : ''}`);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleStatusFilter = (status) => {
    const params = new URLSearchParams(searchParams);
    if (status) {
      params.set('status', status);
    } else {
      params.delete('status');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const search = formData.get('search');
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'acknowledged':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'resolved':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'vercel':
        return 'bg-black text-white';
      case 'sentry':
        return 'bg-purple-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  const getEnvironmentBadge = (environment) => {
    switch (environment) {
      case 'production':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'development':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if ((loading || contextLoading) && errors.length === 0) {
    return (
      <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-zapt-purple mb-4"></div>
            <p className="text-gray-600">Loading errors...</p>
          </div>
        </div>
      </AdminPortalLayout>
    );
  }

  return (
    <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">
              🚨 Error Logs
            </h1>
            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        </div>

        {/* App Filter Banner */}
        {appIdFilter && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-blue-600 font-medium">📱 Filtering by app:</span>
              <span className="text-blue-800 font-semibold">
                {filteredAppName || 'Unknown app'}
              </span>
              <Link
                to={`/admin-portal/app/${appIdFilter}`}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                View app →
              </Link>
            </div>
            <button
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete('appId');
                setSearchParams(params);
              }}
              className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
            >
              ✕ Clear filter
            </button>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <button
            onClick={() => handleStatusFilter('')}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              !statusFilter ? 'border-zapt-purple bg-purple-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
            <p className="text-sm text-gray-600">Total</p>
          </button>
          <button
            onClick={() => handleStatusFilter('new')}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              statusFilter === 'new' ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <p className="text-2xl font-bold text-red-600">{summary.new}</p>
            <p className="text-sm text-gray-600">New</p>
          </button>
          <button
            onClick={() => handleStatusFilter('acknowledged')}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              statusFilter === 'acknowledged' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <p className="text-2xl font-bold text-yellow-600">{summary.acknowledged}</p>
            <p className="text-sm text-gray-600">Acknowledged</p>
          </button>
          <button
            onClick={() => handleStatusFilter('resolved')}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              statusFilter === 'resolved' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <p className="text-2xl font-bold text-green-600">{summary.resolved}</p>
            <p className="text-sm text-gray-600">Resolved</p>
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              name="search"
              defaultValue={searchQuery}
              placeholder="Search by message, path, or project..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-transparent"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 transition-colors font-medium"
            >
              Search
            </button>
          </form>
        </div>

        {/* Errors List */}
        {errors.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-lg">
              {statusFilter || searchQuery 
                ? '🔍 No errors match your filters'
                : '✅ No errors logged yet!'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700 flex-1">Error</span>
                <span className="text-sm font-medium text-gray-700 w-32">App</span>
                <span className="text-sm font-medium text-gray-700 w-24">Status</span>
                <span className="text-sm font-medium text-gray-700 w-24">Time</span>
              </div>
            </div>

            {/* Error Rows */}
            <div className="divide-y divide-gray-200" ref={listContainerRef}>
              {errors.map((error) => (
                <div 
                  key={error.id}
                  ref={(el) => {
                    if (el) errorRefs.current[error.id] = el;
                  }}
                  className={activeErrorId === error.id ? 'ring-2 ring-zapt-purple bg-purple-50 transition-all duration-300' : ''}
                >
                  <div 
                    className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                      activeErrorId === error.id ? 'bg-purple-50' : ''
                    }`}
                    onClick={() => handleErrorClick(error.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSourceBadge(error.source)}`}>
                            {error.source}
                          </span>
                          {error.source === 'vercel' && error.sourceUrl && (
                            <a
                              href={error.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-gray-500 hover:text-gray-700 transition-colors"
                              title="View in Vercel"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                          {error.environment && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getEnvironmentBadge(error.environment)}`}>
                              {error.environment}
                            </span>
                          )}
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {error.path || 'Unknown path'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 truncate">
                          {error.message || 'No message'}
                        </p>
                      </div>
                      <div className="w-32">
                        {error.appId ? (
                          <Link
                            to={`/admin-portal/app/${error.appId}`}
                            className="text-sm text-zapt-purple hover:underline truncate block"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {error.appName || 'Unknown'}
                          </Link>
                        ) : (
                          <span className="text-sm text-gray-400 italic">
                            {error.sourceIdentifier || 'Unlinked'}
                          </span>
                        )}
                      </div>
                      <div className="w-24">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(error.status)}`}>
                          {error.status}
                        </span>
                      </div>
                      <div className="w-24 text-sm text-gray-500">
                        {formatTimestamp(error.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} errors
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    pagination.hasPrevPage
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                          pagination.page === pageNum
                            ? 'bg-zapt-purple text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    pagination.hasNextPage
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
    </AdminPortalLayout>
  );
}
